'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import {
  addDays,
  addMinutes,
  addWeeks,
  isBefore,
  isEqual,
  isToday,
  startOfDay,
  startOfWeek,
  sub,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetOccupiedSlotsFromToday } from '@/lib/react-query/MeetingQueries';
import { cn } from '@/lib/utils';

import type { Horaire } from '../../types/content.types';
import type { MeetingDate } from '../../types/meetings.type';
import { formatDate } from '../../utils/dateUtils';
import {
  PARIS_TZ,
  doesOverlap,
  isCurrentMeetingDate,
  parseSlotInParis,
} from '../../utils/meetingUtils';
import Accordion from '../Accordion';
import Button from '../Button';

const INTERVAL = 30;

type Props = {
  className?: string;
  horaires: Array<Horaire>;
  onSelect: (data: Date | null) => void;
  totalDuration: number;
  defaultSelect?: Date | null;
  existingSelectedHoraire?: {
    start_time: Date;
    end_time: Date;
  } | null;
};

const MeetingSelection = ({
  className = '',
  horaires = [],
  onSelect,
  totalDuration = 20,
  defaultSelect = null,
  existingSelectedHoraire = null,
}: Props) => {
  const { data: occupiedSlots = [] } = useGetOccupiedSlotsFromToday();
  const [weekStart, setWeekStart] = useState<Date>(new Date());
  const [numberWeeksToShow, setNumberWeeksToShow] = useState(1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(defaultSelect ?? null);

  // Convert OccupiedSlot[] to MeetingDate[]
  const meetings: Array<MeetingDate> = occupiedSlots.map((slot) => ({
    start_time: slot.start_time,
    end_time: slot.end_time,
  }));

  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    const daysRemainingInWeek = 7 - today.getDay();
    if (daysRemainingInWeek < 3) {
      setNumberWeeksToShow(2);
    }
  }, [today]);

  useEffect(() => {
    const weekStartDefault = startOfWeek(defaultSelect ?? today, {
      weekStartsOn: 1,
    });

    if (defaultSelect) {
      setWeekStart(weekStartDefault);
    } else if (today.getDay() === 0 || today.getDay() === 6) {
      // Saturday or Sunday — skip to next week as remaining slots are very limited
      setWeekStart(addWeeks(weekStartDefault, 1));
    } else {
      setWeekStart(weekStartDefault);
    }
  }, [defaultSelect, today]);

  const days = useMemo(() => {
    return Array.from({ length: 7 * numberWeeksToShow }).map((_, i) => addDays(weekStart, i));
  }, [weekStart, numberWeeksToShow]);

  const daysMobile = useMemo(() => {
    const numberOfDaysToShow = 7 * numberWeeksToShow;

    return Array.from({ length: numberOfDaysToShow })
      .map((_, i) => addDays(weekStart, i))
      .filter((date) => !isBefore(date, startOfDay(today)));
  }, [numberWeeksToShow, today, weekStart]);

  const handleNextWeek = useCallback(() => {
    const nextWeek = addWeeks(weekStart, 1);
    setWeekStart(nextWeek);
  }, [weekStart]);

  const handlePrevWeek = useCallback(() => {
    const prevWeek = sub(weekStart, { weeks: 1 });
    setWeekStart(prevWeek);
  }, [weekStart]);

  const handleSelectDate = useCallback(
    (date: Date) => {
      if (selectedDate && isEqual(date, selectedDate)) {
        setSelectedDate(existingSelectedHoraire?.start_time ?? null);
        onSelect(existingSelectedHoraire?.start_time ?? null);
        return;
      }

      onSelect(date);
      setSelectedDate(date);
    },
    [existingSelectedHoraire, onSelect, selectedDate],
  );

  const getTimeIntervals = (horaireStr: string, dayDate: Date) => {
    const [startTime = '', endTime = ''] = horaireStr.split(' - ');
    const start = parseSlotInParis(dayDate, startTime);
    const end = sub(parseSlotInParis(dayDate, endTime), { minutes: INTERVAL });
    const times = [];
    let current = start;

    while (current <= end) {
      const nextTime = addMinutes(current, INTERVAL);
      const endMeeting = addMinutes(current, totalDuration);
      const isTaken = doesOverlap(current, endMeeting, meetings, endTime);
      const isCurrent = existingSelectedHoraire
        ? isCurrentMeetingDate(
            current,
            existingSelectedHoraire.start_time,
            existingSelectedHoraire.end_time,
          )
        : false;

      // check if the time is the selected by the user
      const isSelected =
        defaultSelect || selectedDate
          ? isCurrentMeetingDate(
              current,
              selectedDate ?? defaultSelect ?? new Date(),
              totalDuration,
            )
          : false;

      times.push({
        date: current,
        time: formatInTimeZone(current, PARIS_TZ, 'HH:mm'),
        isPast: isBefore(current, new Date()),
        isTaken,
        isCurrent,
        isSelected,
      });

      current = nextTime;
    }

    return times;
  };

  return (
    <div className="relative">
      <p className="mb-4 text-sm text-gray-500">
        Tous les créneaux sont affichés en heure de Paris (France).
      </p>
      <div className="mb-4 hidden items-center justify-between lg:flex">
        <Button onClick={handlePrevWeek} color="ghost" disabled={isBefore(weekStart, today)}>
          <div className="flex items-center gap-1">
            <ChevronLeftIcon className="h-8 w-8" />
            Semaine précédente
          </div>
        </Button>
        <Button color="ghost" onClick={handleNextWeek}>
          <div className="flex items-center gap-1">
            Semaine suivante
            <ChevronRightIcon className="h-8 w-8" />
          </div>
        </Button>
      </div>
      <div className={cn('hidden grid-cols-7 lg:grid lg:gap-6', className)}>
        {days.slice(0, 7).map((day) => {
          const horaire = horaires.find(
            (h) => h.day.toLowerCase() === formatDate(day, 'EEEE').toLowerCase(),
          );

          if (!horaire) return null;

          return (
            <div
              key={horaire.day}
              className={
                isToday(day)
                  ? 'rounded border border-amber-200 bg-amber-100/40 p-2'
                  : 'rounded border border-gray-200 p-2'
              }
            >
              <div className="mb-4">
                <h2 className="text-center font-semibold capitalize">{formatDate(day, 'EEEE')}</h2>
                <h3 className="text-center text-sm capitalize">{formatDate(day, 'dd MMMM')}</h3>
              </div>
              {horaire.is_open && horaire.horaires ? (
                <div className="flex flex-col gap-2">
                  {getTimeIntervals(horaire.horaires, day).map(
                    ({ date, time, isPast, isTaken, isCurrent, isSelected }, idx) => {
                      return (
                        <Button
                          key={idx}
                          color="neutral"
                          size="sm"
                          className={cn(
                            `w-full`,
                            isCurrent
                              ? 'bg-amber-600 outline-2 outline-offset-2 outline-amber-600'
                              : isSelected
                                ? 'bg-amber-700'
                                : null,
                          )}
                          noHover={isSelected || isCurrent}
                          disabled={isPast || isTaken || isCurrent}
                          onClick={() => handleSelectDate(date)}
                        >
                          {time}
                        </Button>
                      );
                    },
                  )}
                </div>
              ) : (
                <p className="text-center font-semibold">Fermé</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-8 lg:hidden">
        {defaultSelect && existingSelectedHoraire ? (
          <Button
            color="secondary"
            size="lg"
            onClick={() => {
              const prevWeekStart = sub(weekStart, { weeks: 1 });
              if (!isBefore(prevWeekStart, startOfDay(today))) {
                setWeekStart(prevWeekStart);
                setNumberWeeksToShow((n) => n + 1);
              }
            }}
            disabled={isBefore(sub(weekStart, { weeks: 1 }), startOfDay(today))}
          >
            Semaine précédente
          </Button>
        ) : null}

        {daysMobile.map((day, dayIdx) => {
          const isFirstItem = defaultSelect
            ? formatDate(day, 'yyyy-MM-dd') === formatDate(defaultSelect, 'yyyy-MM-dd')
            : dayIdx === 0;
          const horaire = horaires.find(
            (h) => h.day.toLowerCase() === formatDate(day, 'EEEE').toLowerCase(),
          );

          if (!horaire || !horaire.is_open) {
            return null;
          }

          const intervals = horaire.horaires ? getTimeIntervals(horaire.horaires, day) : [];
          const allUnavailable =
            intervals.length > 0 &&
            intervals.every(({ isPast, isTaken, isCurrent }) => isPast || isTaken || isCurrent);

          return (
            <Accordion
              key={day.toString()}
              title={<span className="capitalize">{formatDate(day, 'EEEE dd MMMM')}</span>}
              isOpenDefault={isFirstItem}
            >
              {!allUnavailable && horaire.horaires ? (
                <div className="grid grid-cols-3 gap-4">
                  {getTimeIntervals(horaire.horaires, day).map(
                    ({ date, time, isPast, isTaken, isCurrent, isSelected }, idx) => {
                      if (isPast || isTaken) {
                        return null;
                      }

                      return (
                        <Button
                          key={idx}
                          color="ghost"
                          className={cn(
                            'w-full',
                            isCurrent
                              ? 'bg-amber-300 outline-2 outline-offset-2 outline-amber-300'
                              : isSelected
                                ? 'bg-amber-400'
                                : 'bg-white',
                          )}
                          noHover={isSelected || isCurrent}
                          disabled={false}
                          onClick={() => handleSelectDate(date)}
                        >
                          {time}
                        </Button>
                      );
                    },
                  )}
                </div>
              ) : (
                <p>Aucun créneau disponible</p>
              )}
            </Accordion>
          );
        })}

        <Button color="secondary" size="lg" onClick={() => setNumberWeeksToShow((n) => n + 1)}>
          Afficher plus de dates
        </Button>
      </div>
    </div>
  );
};

export default MeetingSelection;
